import { useEffect, useState } from "react";
import { roleLabel } from "@/lib/roles";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { useAuth } from "@/context/AuthContext";
import { Plus, PencilSimple, Trash, User as UserIcon, ShieldCheck, Star } from "@phosphor-icons/react";

const EMPTY = { email: "", name: "", password: "", role: "operator", warehouse_id: "", is_default_odt_evasore: false };

function renderWarehouseCell(row, wName) {
  if (row.role !== "operator") return "—";
  if (!row.warehouse_id) return <span className="text-amber-700">— non assegnato</span>;
  return wName(row.warehouse_id);
}

export default function Users() {
  const { user: currentUser } = useAuth();
  const [rows, setRows] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    try {
      const [{ data: users }, { data: whs }] = await Promise.all([
        api.get("/users"), api.get("/warehouses"),
      ]);
      setRows(users);
      setWarehouses(whs);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openNew = () => { setEditing(null); setForm(EMPTY); setShowForm(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({ email: r.email, name: r.name, password: "", role: r.role, warehouse_id: r.warehouse_id || "", is_default_odt_evasore: !!r.is_default_odt_evasore });
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        const payload = { name: form.name, email: form.email, role: form.role, warehouse_id: form.warehouse_id || null, is_default_odt_evasore: !!form.is_default_odt_evasore };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editing.id}`, payload);
        toast.success("Utente aggiornato");
      } else {
        const payload = { ...form };
        if (!payload.warehouse_id) delete payload.warehouse_id;
        await api.post("/users", payload);
        toast.success("Utente creato");
      }
      setShowForm(false); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const wName = (id) => (warehouses.find((w) => w.id === id) || {}).name || "—";
  const mobileWhs = warehouses.filter((w) => w.type === "mobile");

  const remove = async (r) => {
    if (!window.confirm(`Eliminare l'utente ${r.email}?`)) return;
    try {
      await api.delete(`/users/${r.id}`);
      toast.success("Utente eliminato");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <PageHeader
        title="Utenti"
        subtitle="Gestione account e ruoli — solo admin"
        action={
          <button data-testid="new-user-btn" onClick={openNew} className="text-sm px-4 py-2.5 bg-[#002FA7] hover:bg-[#002480] text-white transition-colors duration-150 flex items-center gap-2">
            <Plus size={16} weight="bold" /> <span className="hidden sm:inline">Nuovo Utente</span><span className="sm:hidden">Nuovo</span>
          </button>
        }
      />

      <div className="p-4 md:p-8">
        <div className="bg-white border border-neutral-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 border-b border-neutral-200">
              <tr className="text-left">
                <th className="px-4 py-3 label-eyebrow">Utente</th>
                <th className="px-4 py-3 label-eyebrow">Email</th>
                <th className="px-4 py-3 label-eyebrow">Ruolo</th>
                <th className="px-4 py-3 label-eyebrow">Furgone Assegnato</th>
                <th className="px-4 py-3 label-eyebrow hidden md:table-cell">Creato</th>
                <th className="px-4 py-3 label-eyebrow text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan="6" className="px-4 py-12 text-center text-neutral-500">Nessun utente.</td></tr>
              ) : rows.map((r) => {
                let roleIcon = <UserIcon size={16} className="text-neutral-500" />;
                if (r.role === "admin") roleIcon = <ShieldCheck size={16} weight="bold" className="text-[#002FA7]" />;
                else if (r.role === "gestore") roleIcon = <ShieldCheck size={16} weight="regular" className="text-emerald-600" />;
                let roleBadge = "bg-neutral-200 text-neutral-700";
                if (r.role === "admin") roleBadge = "bg-[#002FA7] text-white";
                else if (r.role === "gestore") roleBadge = "bg-emerald-600 text-white";
                return (
                <tr key={r.id} className="border-b border-neutral-100 hover:bg-neutral-50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {roleIcon}
                      <span className="font-medium">{r.name}</span>
                      {r.is_default_odt_evasore && (
                        <span
                          data-testid={`user-evasore-badge-${r.id}`}
                          title="Evasore ODT standard"
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-100 text-amber-800 text-[10px] font-semibold uppercase tracking-wider"
                        >
                          <Star size={10} weight="fill" /> Evasore ODT
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-neutral-600">{r.email}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-1 text-[10px] font-semibold uppercase tracking-wider ${roleBadge}`}>
                      {roleLabel(r.role)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-neutral-600">
                    {renderWarehouseCell(r, wName)}
                  </td>
                  <td className="px-4 py-3 text-neutral-500 font-mono text-xs hidden md:table-cell">{r.created_at ? new Date(r.created_at).toLocaleDateString("it-IT") : "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <button data-testid={`edit-user-${r.id}`} onClick={() => openEdit(r)} className="p-1.5 hover:bg-neutral-200 transition-colors mr-1"><PencilSimple size={16} /></button>
                    <button
                      data-testid={`delete-user-${r.id}`}
                      onClick={() => remove(r)}
                      disabled={r.id === currentUser?.id}
                      className="p-1.5 hover:bg-red-100 text-red-600 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                      title={r.id === currentUser?.id ? "Non puoi eliminare il tuo account" : ""}
                    >
                      <Trash size={16} />
                    </button>
                  </td>
                </tr>
              );})}
            </tbody>
          </table>
        </div>

      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-2 md:p-4">
          <form onSubmit={submit} className="bg-white border border-neutral-200 shadow-xl w-full max-w-md max-h-[95vh] flex flex-col overflow-y-auto" data-testid="user-form">
            <div className="p-6 border-b border-neutral-200">
              <div className="label-eyebrow mb-1">{editing ? "Modifica" : "Nuovo"}</div>
              <h2 className="font-display font-bold text-xl tracking-tight">Utente</h2>
            </div>
            <div className="p-6 space-y-4">
              <label className="block">
                <span className="label-eyebrow block mb-2">Nome *</span>
                <input required data-testid="user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2 border border-neutral-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
              </label>
              <label className="block">
                <span className="label-eyebrow block mb-2">Email *</span>
                <input required type="email" data-testid="user-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full px-3 py-2 border border-neutral-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
              </label>
              <label className="block">
                <span className="label-eyebrow block mb-2">Password {editing ? "(lascia vuoto per non modificare)" : "*"}</span>
                <input required={!editing} type="password" data-testid="user-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="••••••••" className="w-full px-3 py-2 border border-neutral-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
              </label>
              <div>
                <span className="label-eyebrow block mb-2">Ruolo *</span>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {[["viewer", "Assistente Tecnico", UserIcon], ["operator", "Operatore", UserIcon], ["gestore", "Gestore", ShieldCheck], ["admin", "Admin", ShieldCheck]].map(([v, l, I]) => (
                    <button
                      key={v}
                      type="button"
                      data-testid={`user-role-${v}`}
                      onClick={() => setForm({ ...form, role: v })}
                      className={`flex items-center justify-center gap-2 py-2.5 border transition-colors text-sm font-medium ${form.role === v ? "border-[#002FA7] bg-[#002FA7] text-white" : "border-neutral-300 bg-white hover:bg-neutral-50"}`}
                    >
                      <I size={16} weight="bold" /> {l}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-neutral-500 mt-2">
                  {(() => {
                    if (form.role === "admin") return "Accesso completo: crea/modifica prodotti, magazzini, utenti, audit log e azzera dati.";
                    if (form.role === "gestore") return "Accesso completo ai dati (prodotti, magazzini, movimenti, clienti, commesse) ma NON può gestire utenti né vedere l'audit log.";
                    if (form.role === "viewer") return "Assistente Tecnico: può consultare tutte le viste, scaricare PDF, esportare Excel e inserire ODT, ma non può modificare dati del magazzino.";
                    return "Vede solo il proprio magazzino mobile e può effettuare solo scarichi a commessa.";
                  })()}
                </p>
              </div>
              {form.role === "operator" && (
                <label className="block">
                  <span className="label-eyebrow block mb-2">Furgone assegnato</span>
                  <select
                    data-testid="user-warehouse"
                    value={form.warehouse_id}
                    onChange={(e) => setForm({ ...form, warehouse_id: e.target.value })}
                    className="w-full px-3 py-2 border border-neutral-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                  >
                    <option value="">— Nessuno (accesso limitato) —</option>
                    {mobileWhs.map((w) => (
                      <option key={w.id} value={w.id}>{w.name}{w.owner_name ? ` · ${w.owner_name}` : ""}</option>
                    ))}
                  </select>
                  <p className="text-xs text-neutral-500 mt-1">
                    Il tecnico vedrà solo questo furgone e potrà scaricare i materiali a commessa.
                  </p>
                </label>
              )}
              {(form.role === "admin" || form.role === "gestore") && (
                <label className="flex items-start gap-3 p-3 border border-amber-200 bg-amber-50 cursor-pointer hover:bg-amber-100 transition-colors" data-testid="user-evasore-row">
                  <input
                    type="checkbox"
                    data-testid="user-evasore-checkbox"
                    checked={!!form.is_default_odt_evasore}
                    onChange={(e) => setForm({ ...form, is_default_odt_evasore: e.target.checked })}
                    className="mt-0.5 w-4 h-4 accent-amber-600"
                  />
                  <div>
                    <div className="flex items-center gap-1.5 font-semibold text-amber-900 text-sm">
                      <Star size={14} weight="fill" className="text-amber-600" /> Evasore ODT standard
                    </div>
                    <div className="text-xs text-amber-800 mt-0.5">
                      Riceverà una mail quando un nuovo ODT viene inserito e verrà preselezionato nel form "Nuovo ODT". Può esserci un solo evasore standard: impostando questo utente, il precedente viene disattivato.
                    </div>
                  </div>
                </label>
              )}
            </div>
            <div className="p-6 pt-0 flex justify-end gap-2">
              <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-sm border border-neutral-300 hover:bg-neutral-100 transition-colors">Annulla</button>
              <button data-testid="user-submit" type="submit" className="px-4 py-2 text-sm bg-[#002FA7] hover:bg-[#002480] text-white transition-colors">{editing ? "Salva" : "Crea"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
